import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  getEditableListing,
  updatePrivateListing,
  markPrivateListingSold,
} from "@/lib/privateListingEdit.functions";

export const Route = createFileRoute("/editeaza-anunt/$listingId/")({
  validateSearch: (s) => z.object({ token: z.string().optional() }).parse(s),
  head: () => ({
    meta: [
      { title: "Editează anunțul | RO Auto Market" },
      { name: "description", content: "Modifică prețul, kilometrajul sau descrierea anunțului tău, fără cont." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Editează anunțul | RO Auto Market" },
      { property: "og:description", content: "Gestionează anunțul tău auto printr-un link privat." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: EditPage,
});

type Listing = Awaited<ReturnType<typeof getEditableListing>>;

function EditPage() {
  const { listingId } = Route.useParams();
  const { token = "" } = Route.useSearch();
  const load = useServerFn(getEditableListing);
  const save = useServerFn(updatePrivateListing);
  const sold = useServerFn(markPrivateListingSold);

  const [listing, setListing] = useState<Listing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState("");
  const [mileage, setMileage] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    load({ data: { id: listingId, token } })
      .then((l) => {
        setListing(l);
        setPrice(String(l.price ?? ""));
        setMileage(String(l.mileage ?? ""));
        setDescription(l.description);
      })
      .catch(() => setError("Linkul nu este valid. Scrie-ne la contact@roautomarket.ro sau sună la +40 732 963 134."));
  }, [listingId, token]);

  const onSave = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await save({ data: { id: listingId, token, price: Number(price), mileage: Number(mileage), description } });
      setMsg("Modificările au fost salvate.");
    } catch {
      setMsg("Verifică datele: preț minim 100, descriere de cel puțin 20 de caractere.");
    } finally {
      setBusy(false);
    }
  };

  const onSold = async () => {
    if (!confirm("Confirmi că mașina a fost vândută? Anunțul nu va mai apărea pe site.")) return;
    setBusy(true);
    try {
      await sold({ data: { id: listingId, token } });
      setListing((l) => (l ? { ...l, status: "sold" } : l));
      setMsg("Anunțul a fost marcat ca vândut. Felicitări!");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="font-serif text-3xl mb-6">Editează anunțul</h1>
        {error && <p className="text-destructive">{error}</p>}
        {!error && !listing && <p className="text-muted-foreground">Se încarcă...</p>}
        {listing && (
          <div className="space-y-6">
            <div className="flex gap-4 items-center rounded-lg border border-border bg-card p-4">
              {listing.photo && <img src={listing.photo} alt={`${listing.make} ${listing.model}`} className="h-20 w-28 rounded object-cover" />}
              <div>
                <p className="font-semibold">{listing.make} {listing.model} {listing.year}</p>
                <p className="text-sm text-muted-foreground">
                  {listing.status === "sold" ? "Vândut" : listing.status === "active" ? "Activ" : "Inactiv"}
                </p>
                <Link to="/listing/$slug" params={{ slug: listing.id }} className="text-sm text-primary underline">
                  Vezi anunțul public
                </Link>
              </div>
            </div>

            {listing.status !== "sold" && (
              <>
                <label className="block space-y-1">
                  <span className="text-sm font-medium">Preț ({listing.currency})</span>
                  <Input type="number" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} />
                </label>
                <label className="block space-y-1">
                  <span className="text-sm font-medium">Kilometraj (km)</span>
                  <Input type="number" inputMode="numeric" value={mileage} onChange={(e) => setMileage(e.target.value)} />
                </label>
                <label className="block space-y-1">
                  <span className="text-sm font-medium">Descriere</span>
                  <Textarea rows={10} value={description} onChange={(e) => setDescription(e.target.value)} />
                </label>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={onSave} disabled={busy}>Salvează modificările</Button>
                  <Button variant="outline" onClick={onSold} disabled={busy}>Am vândut mașina</Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Pentru schimbarea pozelor, scrie-ne la contact@roautomarket.ro.
                </p>
              </>
            )}
            {msg && <p className="text-sm">{msg}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
